import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';

import Input from '../components/Input';
import Button from '../components/Button';
import { requestRegistrationOTP, verifyRegistrationOTP } from '../services/api';
import { trackOtpVerifiedConversion } from '../utils/gtag';
import { colors } from '../theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const RegisterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1rem;
  background-color: #f8f9fa;
  background-image: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const RegisterCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 450px;
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    max-width: 90%;
    margin: 0 auto;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, ${colors.darkPurple}, ${colors.softPurple});
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  
  img {
    max-width: 140px;
    height: auto;
    
    @media (max-width: 480px) {
      max-width: 120px;
    }
  }
`;

const FormContainer = styled.div`
  width: 100%;
  animation: ${props => props.animate ? slideIn : 'none'} 0.4s ease-out;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 1.2rem;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;
  box-sizing: border-box;
`;

const StyledButton = styled(Button)`
  margin-top: 0.5rem;
  
  @media (max-width: 480px) {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
`;

const LoginLink = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: ${colors.mediumGray};
  
  a {
    color: ${colors.softPurple};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 480px) {
    margin-top: 1.2rem;
    font-size: 0.85rem;
  }
`;

const InfoText = styled.div`
  font-size: 0.9rem;
  color: ${colors.mediumGray};
  text-align: center;
  margin: 0.5rem 0;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ResendLink = styled.span`
  color: ${colors.softPurple};
  margin-left: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    otp: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_campaignid: '',
    utm_adid: '',
    program: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('register'); // 'register' or 'otp'
  const [registrationId, setRegistrationId] = useState(null);
  const [expiryMinutes, setExpiryMinutes] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    // Capture UTM parameters and program from URL on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmCampaignId = urlParams.get('utm_campaignid');
    const utmAdId = urlParams.get('utm_adid');
    const program = urlParams.get('program');
    
    if (utmSource || utmMedium || utmCampaign || utmCampaignId || utmAdId || program) {
      setFormData(prev => ({
        ...prev,
        utm_source: utmSource || '',
        utm_medium: utmMedium || '',
        utm_campaign: utmCampaign || '',
        utm_campaignid: utmCampaignId || '',
        utm_adid: utmAdId || '',
        program: program || ''
      }));
    }
  }, []);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(prevCount => prevCount - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setResendDisabled(false);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCountdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow digits and limit to 10 characters
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: digitsOnly.slice(0, 10) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setError('');
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Validate phone number
    if (formData.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    
    try {
      // Request OTP for registration
      const response = await requestRegistrationOTP(formData);
      setRegistrationId(response.registration_id);
      setExpiryMinutes(response.expiry_minutes);
      setStep('otp');
      
      // Disable resend button and start countdown
      setResendDisabled(true);
      setResendCountdown(120); // 2 minutes in seconds
      
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail.user_exists) {
        // User exists, show login prompt with program_id
        const loginUrl = `/login?program=${formData.program}&phone=${encodeURIComponent(formData.phone)}`;
        setError(
          <span>
            {error.response.data.detail.message} <Link to={loginUrl}>Click here to login</Link>
          </span>
        );
      } else {
        setError(error.response?.data?.detail || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      // Verify OTP for registration
      const userData = await verifyRegistrationOTP(registrationId, formData.otp);
      
      trackOtpVerifiedConversion();
      
      // Save user data to localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      console.error('OTP verification error:', error);
      setError(error.response?.data?.detail || 'Invalid OTP. Please try again.');
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendDisabled) return;
    
    try {
      setLoading(true);
      
      // Request OTP again
      const response = await requestRegistrationOTP(formData);
      setRegistrationId(response.registration_id);
      setExpiryMinutes(response.expiry_minutes);
      
      // Disable resend button and start countdown
      setResendDisabled(true);
      setResendCountdown(120); // 2 minutes in seconds
      
      toast.success('OTP resent successfully!');
    } catch (error) {
      console.error('Resend OTP error:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getLoginUrl = () => {
    const params = new URLSearchParams();
    if (formData.program) params.append('program', formData.program);
    if (formData.phone) params.append('phone', formData.phone);
    return `/login?${params.toString()}`;
  };

  return (
    <RegisterContainer>
      <RegisterCard>

        {formData.program === 'iitj-bsc' ? (
          <Logo><img src="../../assets/iitjodhpur-logo.png" alt="IITJ BSC Logo" width={140} height={60} /></Logo>
        ) : (
          <Logo><img src="../../assets/fut-logo.png" alt="Futurense Logo" width={140} height={60} /></Logo>
        )}
        {step === 'register' ? (
          <FormContainer>
            <Form onSubmit={handleRegisterSubmit}>
              <StyledInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name"
                required
              />
              <StyledInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address"
                required
              />
              <StyledInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number"
                required
              />
              
              {error && <InfoText style={{ color: 'red' }}>{error}</InfoText>}
              
              <StyledButton 
                type="submit" 
                disabled={loading}
                fullWidth
              >
                {loading ? 'Processing...' : 'Register'}
              </StyledButton>
            </Form>
          </FormContainer>
        ) : (
          <FormContainer animate>
            <InfoText>
              An OTP has been sent to {formData.phone}. 
              It will expire in {expiryMinutes} minutes.
            </InfoText>
            <Form onSubmit={handleOtpSubmit}>
              <StyledInput
                type="text"
                name="otp"
                value={formData.otp}
                onChange={handleChange}
                placeholder="Enter the OTP"
                required
              />
              
              {error && <InfoText style={{ color: 'red' }}>{error}</InfoText>}
              
              <StyledButton 
                type="submit" 
                disabled={loading}
                fullWidth
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </StyledButton>
              <InfoText>
                Didn't receive the OTP?
                <ResendLink 
                  onClick={handleResendOTP}
                  disabled={resendDisabled}
                  style={{ 
                    cursor: resendDisabled ? 'not-allowed' : 'pointer',
                    opacity: resendDisabled ? 0.5 : 1 
                  }}
                >
                  {resendDisabled ? `Resend (${resendCountdown}s)` : 'Resend'}
                </ResendLink>
              </InfoText>
            </Form>
          </FormContainer>
        )}
        
        <LoginLink>
          Already have an account? <Link to={getLoginUrl()}>Login here</Link>
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register;
